/**
 * AutoPushSubscribe — silent auto-opt-in for push notifications with a
 * mobile-friendly visible fallback banner.
 *
 * Loaded once inside <App>. Two paths:
 *   1. Silent — attach a one-shot click listener; on the first user gesture
 *      after 2 seconds, request permission and subscribe.
 *   2. Visible fallback — if we're still not subscribed 6 seconds after
 *      landing (common on mobile where users scroll instead of tapping),
 *      render a bottom banner with a clear "Enable" button.
 *
 * Anonymous visitors are supported via /api/push/subscribe-guest.
 * LocalStorage remembers granted/denied/dismissed so we never re-prompt.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, X } from "lucide-react";

const STATE_KEY = "htp_push_auto_state";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function AutoPushSubscribe() {
  const { user } = useAuth() || {};
  const [supported, setSupported] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;

    const stored = localStorage.getItem(STATE_KEY);
    if (stored === "denied" || Notification.permission === "denied") {
      if (Notification.permission === "denied") localStorage.setItem(STATE_KEY, "denied");
      return;
    }
    if (stored === "granted" || Notification.permission === "granted") {
      // Already granted — silently make sure a subscription exists.
      subscribeNow(true).then(() => setDone(true));
      return;
    }

    let disposed = false;
    let attempted = false;

    const tryOnce = async () => {
      if (attempted || disposed) return;
      attempted = true;
      const success = await subscribeNow(false);
      if (success) setDone(true);
      else attempted = false; // allow the banner to try again
    };

    // Silent path — first user gesture after 2s
    const armTimer = setTimeout(() => {
      if (disposed) return;
      const opts = { once: true, capture: true, passive: true };
      window.addEventListener("click", tryOnce, opts);
      window.addEventListener("touchend", tryOnce, opts);
    }, 2000);

    // Visible fallback — 6s later, if we haven't subscribed yet, show banner
    const bannerTimer = setTimeout(() => {
      if (disposed) return;
      const st = localStorage.getItem(STATE_KEY);
      if (st === "granted" || st === "dismissed") return;
      if (Notification.permission === "default") setShowBanner(true);
    }, 6000);

    return () => {
      disposed = true;
      clearTimeout(armTimer);
      clearTimeout(bannerTimer);
    };
  }, [user]);

  async function subscribeNow(silent) {
    setBusy(true);
    try {
      // Permission request must happen inside a user gesture on Chrome/Safari.
      if (Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          localStorage.setItem(STATE_KEY, perm === "denied" ? "denied" : "dismissed");
          return false;
        }
      }
      if (Notification.permission !== "granted") return false;

      // Ensure the service worker exists — fixes brand-new-visitor case.
      try { await navigator.serviceWorker.register("/sw.js"); } catch (e) { console.debug("SW register:", e); return false; }
      const { data: keyResp } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
        });
      }
      const body = {
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))),
          auth:   btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))),
        },
        user_agent: navigator.userAgent,
      };
      const endpoint = user ? "/push/subscribe" : "/push/subscribe-guest";
      try {
        await api.post(endpoint, body);
      } catch {
        try { await api.post("/push/subscribe-guest", body); } catch { /* silent */ }
      }
      localStorage.setItem(STATE_KEY, "granted");
      setShowBanner(false);
      return true;
    } catch (err) {
      if (!silent) console.debug("Subscribe failed:", err);
      return false;
    } finally {
      setBusy(false);
    }
  }

  const dismiss = () => {
    localStorage.setItem(STATE_KEY, "dismissed");
    setShowBanner(false);
  };

  if (!supported || done || !showBanner) return null;

  return (
    <div
      data-testid="push-enable-banner"
      className="fixed bottom-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:max-w-md z-40 bg-[#0B1120] border border-brand-orange/60 shadow-2xl px-4 py-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-brand-orange/20 flex items-center justify-center shrink-0">
          <Bell size={18} className="text-brand-orange"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black uppercase text-sm leading-tight">Get HTP alerts on your phone</div>
          <div className="text-[11px] text-white/60 mt-0.5">Instant notifications — bookings, tournaments, offers.</div>
        </div>
        <button
          onClick={() => subscribeNow(false)}
          disabled={busy}
          data-testid="push-enable-btn"
          className="bg-brand-orange text-black hover:bg-white disabled:opacity-40 px-4 py-2 text-[11px] font-bold uppercase tracking-widest"
        >
          {busy ? "…" : "Enable"}
        </button>
        <button onClick={dismiss} data-testid="push-enable-dismiss" aria-label="Dismiss" className="text-white/50 hover:text-white p-1">
          <X size={14}/>
        </button>
      </div>
    </div>
  );
}
